import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Group, Stack, Text } from '@mantine/core';
import { BRAND_ASSET_SLOTS, BRAND_ASSET_SPECS, type BrandAssetSlot } from '@seo/contracts';
import { API_BASE, publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import classes from '../pages/EntityPage.module.css';

/**
 * Слоти медіа бренду — останні 15 ваги відсотка готовності.
 *
 * `computeFill` рахує медіа з першого дня, а привʼязати файл до слота не було чим: ні
 * сервісної функції, ні процедури, ні екрана. Бренд, заведений через CRM, упирався в 85 зі
 * 100 — це була стеля, а не поточний стан.
 *
 * Слоти, а не масив файлів (мокап, рядки 4520–4525): фіксований іменований перелік, текст
 * вимог під кожним і видимий стан «✗ немає». Порожній слот — робочий стан, а не помилка:
 * бренд без третього банера збирається, просто набирає менше.
 *
 * Два кроки, а не один: байти йдуть у `POST /media/upload`, який єдиний їх санітизує (SVG зі
 * скриптом і PNG із вантажем у хвості — причина, чому прямого PUT у сховище не існує), і
 * лише повернутий `mediaFileId` кладеться в слот. Тому один логотип може стояти в кількох
 * слотах, не дублюючись у сховищі.
 */

export interface AssetSlotRow {
  slot: string;
  spec: string;
  state: string;
  mediaFileId: string | null;
  fileKey: string | null;
  mime: string | null;
  width: number | null;
  height: number | null;
}

export interface AssetSlotsProps {
  brandId: string;
  slots: AssetSlotRow[];
}

const SLOT_LABELS: Record<string, string> = {
  logo: 'Логотип',
  favicon: 'Фавікон',
  main_banner: 'Головний банер',
  secondary_banner_1: 'Банер 2',
  secondary_banner_2: 'Банер 3',
  secondary_banner_3: 'Банер 4',
  mobile_screenshot: 'Мобільний скрин',
  signup_form: 'Форма реєстрації',
};

interface UploadResult {
  mediaFileId?: string;
  message?: string;
  error?: string;
}

export function AssetSlots({ brandId, slots }: AssetSlotsProps): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const canWrite = useCan('databases', 'write');
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});
  const [busySlot, setBusySlot] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const attach = useMutation(
    trpc.brands.asset.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.brands.pathKey() });
      },
    }),
  );

  const onFile = async (slot: string, file: File): Promise<void> => {
    setBusySlot(slot);
    setUploadError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      // Слот їде разом із байтами: `processUpload` звіряє з ним допустимі формати.
      body.append('slot', slot);
      const res = await fetch(`${API_BASE}/media/upload`, {
        method: 'POST',
        body,
        credentials: 'include',
      });
      const json = (await res.json()) as UploadResult;
      if (!res.ok || json.mediaFileId === undefined) {
        setUploadError(json.message ?? json.error ?? 'Не вдалося завантажити файл.');
        return;
      }
      attach.mutate({ id: brandId, slot: slot as BrandAssetSlot, mediaFileId: json.mediaFileId });
    } catch (err) {
      setUploadError(publicMessageOf(err));
    } finally {
      setBusySlot(null);
    }
  };

  const bySlot = new Map(slots.map((row) => [row.slot, row]));

  return (
    <Stack gap={6} data-testid="asset-slots">
      <Text className={classes.dim}>
        Медіа — вага 15 у відсотку готовності; порожній слот не помилка, але й не заповнений
      </Text>
      {BRAND_ASSET_SLOTS.map((slot) => {
        const row = bySlot.get(slot);
        const present = row?.state === 'present';
        return (
          <Group key={slot} gap={8} data-testid={`asset-slot-${slot}`}>
            <Text size="sm" style={{ width: 150 }}>
              {SLOT_LABELS[slot] ?? slot}
            </Text>
            <Badge
              size="xs"
              color={present ? 'mint' : 'slate'}
              data-testid={`asset-state-${slot}`}
            >
              {present ? 'є' : '✗ немає'}
            </Badge>
            {/*
              * Вимога береться з контракту, якщо сервер її не прислав: перелік слотів
              * фіксований, тож слот без тексту вимоги — це слот, про який оператор не знає,
              * що саме туди класти.
              */}
            <Text className={classes.dim} style={{ flex: 1, minWidth: 0 }}>
              {row?.spec !== undefined && row.spec !== '' ? row.spec : BRAND_ASSET_SPECS[slot]}
            </Text>
            {row?.width === null || row?.height === null || row === undefined ? null : (
              <Text className={classes.mono} data-testid={`asset-size-${slot}`}>
                {row.width}×{row.height}
              </Text>
            )}
            {canWrite ? (
              <>
                <input
                  ref={(element) => {
                    inputs.current[slot] = element;
                  }}
                  type="file"
                  accept="image/*"
                  hidden
                  data-testid={`asset-file-${slot}`}
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    if (file) void onFile(slot, file);
                    // Скидаємо, щоб повторний вибір того самого файлу знову дав `change`.
                    event.currentTarget.value = '';
                  }}
                />
                <Button
                  size="compact-xs"
                  variant="default"
                  loading={busySlot === slot}
                  data-testid={`asset-upload-${slot}`}
                  onClick={() => {
                    inputs.current[slot]?.click();
                  }}
                >
                  {present ? 'Замінити' : 'Завантажити'}
                </Button>
                {present ? (
                  /*
                   * Звільнити слот — не те саме, що видалити файл: на нього може посилатися
                   * вже опублікована сторінка, тож запис у `media_files` лишається.
                   */
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    data-testid={`asset-clear-${slot}`}
                    onClick={() => {
                      attach.mutate({ id: brandId, slot, mediaFileId: null });
                    }}
                  >
                    звільнити
                  </Button>
                ) : null}
              </>
            ) : null}
          </Group>
        );
      })}

      {uploadError === null && attach.error === null ? null : (
        <Text className={classes.danger} data-testid="asset-error" role="alert">
          {uploadError ?? publicMessageOf(attach.error)}
        </Text>
      )}
    </Stack>
  );
}
